var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { applyLinkWrappers, buildExternalOffer, getMappedKeyValues, identifyLinkWrappersForOffer, isTrackonomicsLinkWrapperConfig, LinkWrappers, OfferSelectionContainer, } from '@themaven-net/commerce-shared';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, unsafeStatic } from 'lit/static-html.js';
import BaseOfferAwareComponent from './baseOfferAwareComponent.js';
export default class BaseCommerceLink extends BaseOfferAwareComponent {
    constructor() {
        super(...arguments);
        /**
         * Page level link wrapping configs that will be merged into any specified wrapper configuration
         * on the link itself. This must be filled out by the implementing web component
         * @see retrieveEnvironmentSettings()
         */
        this.pageLevelWrapperConfigs = {};
        // pls to adopt children existing in markup
        this.adoptChildren = true;
        /**
         * The functions to use to wrap links. This can be overridden to implement per-environment wrapping
         */
        this.linkWrapperFns = LinkWrappers;
    }
    get offerCount() {
        return 1;
    }
    /**
     * Template method, override to add your click analytics behaviour
     */
    sendClickAnalytics(_clickEvent) {
        return;
    }
    /**
     * Template method, return any dynamic properties that need to be on the link
     */
    getDynamicAttributesMapper() {
        return {};
    }
    retrieveEnvironmentSettings() {
        // intentional no-op, override to implement per-environment settings
    }
    /**
     * Overriding base select offer logic so we can supply stub offers or the solo supplied offer if need be
     */
    selectOffers(item) {
        if (this.stubOffer !== undefined) {
            // we generated a stub from a supplied url
            this.offerSelection = new OfferSelectionContainer([this.stubOffer], [], [], 1);
            return;
        }
        if (this.config?.offer !== undefined) {
            // we have a specific offer
            this.offerSelection = new OfferSelectionContainer([this.config.offer], [], [], 1);
            return;
        }
        // resume default flow
        super.selectOffers(item);
    }
    render() {
        const decorated = this.buildDecoratedHref();
        return html `<a
            href="${decorated}"
            target="${ifDefined(this.target)}"
            rel="${ifDefined(this.rel)}"
            ${this.renderExtraAttributes()}
            class="${this.getClassnames()}"
            @click=${(event) => this.handleLinkClick(event)}
        ></a>`;
    }
    /**
     * Filter off any non-anchor clicks and pass it to the implemented click analytics method
     */
    handleLinkClick(clickEvent) {
        if (!(clickEvent.target instanceof HTMLAnchorElement)) {
            return;
        }
        this.sendClickAnalytics(clickEvent);
    }
    /**
     * Template method, override it to add your decoration
     */
    buildDecoratedHref() {
        if (this.wrappedHref !== undefined) {
            return this.wrappedHref;
        }
        return this.offerSelection?.primaryOffer?.url ?? '#';
    }
    /**
     * Set a dynamic attribute to be added into the rendered tag
     */
    setDynamicAttr(attr, value) {
        if (this.dynamicAttrs === undefined) {
            this.dynamicAttrs = {};
        }
        this.dynamicAttrs[attr] = value;
    }
    /**
     * When the component first gets connected, we need to identify the subject offer and
     * buffer the move the original children.  This is so they can later be moved into this
     * component's anchor tag. This is necessary because we don't want
     * to use the shadow DOM, and therefore cannot use a slot. If an original child is
     * an anchor tag, retain its children.
     */
    connectedCallback() {
        super.connectedCallback();
        this.retrieveEnvironmentSettings();
        this.checkNonOfferUrls();
        this.selectOffers(this.item);
        this.wrapHref();
    }
    /**
     * Identify any nodes that should be appended on render
     */
    filterAppendList(child, renderedAnchor) {
        if (child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) {
            // prevent empty nodes because reasons
            return [];
        }
        // If it's not an anchor tag, just return it with all of the children
        if (child.nodeName !== 'A') {
            return [child];
        }
        // if the "child" is our anchor node, which shouldn't happen
        if (renderedAnchor.isSameNode(child)) {
            return [];
        }
        const results = [];
        // if it is an anchor tag, grab all the children
        for (let i = 0; i < child.childNodes.length; i++) {
            results.push(child.childNodes[i]);
        }
        // shed the containing A tag at the root level, but keep the children
        try {
            this.removeChild(child);
        }
        catch {
            /** noop for re-add **/
        }
        results.forEach((grandChild) => child.removeChild(grandChild));
        return results;
    }
    /**
     * Determine what URL to use for the anchor element
     *
     * The order of use is:
     *  child anchor tag -> offer in config -> preset href property
     */
    checkNonOfferUrls() {
        const firstAnchor = this.querySelector('a');
        if (firstAnchor !== null) {
            this.stubOffer = buildExternalOffer(firstAnchor.href);
            return;
        }
        if (this.href !== undefined) {
            this.stubOffer = buildExternalOffer(this.href);
            return;
        }
    }
    /**
     * Get the classnames to add to the anchor element
     */
    getClassnames() {
        let classnames = 'm-product--commerce-link appendTarget';
        if (this.classnames !== undefined) {
            classnames += ` ${this.classnames}`;
        }
        return classnames;
    }
    /**
     * Return any data-* or other attributes that need to be added to the rendered link
     */
    renderExtraAttributes() {
        const values = getMappedKeyValues(this.getDynamicAttributesMapper());
        // start off with dynamic attributes
        const results = values.map(({ key, value }) => `${key}="${value}"`);
        // add in extra attributes that have been set
        if (this.dynamicAttrs !== undefined) {
            Object.entries(this.dynamicAttrs).forEach(([key, value]) => results.push(`${key}="${value}"`));
        }
        return unsafeStatic(results.join(' '));
    }
    /**
     * Apply configured link wrappers to the detected url prior to render
     *
     * This should come before decorating the link.  Override to add
     * default wrapping configs by checking the window or context
     */
    wrapHref() {
        if (this.offerSelection === undefined) {
            this.selectOffers(); //just to try careful
        }
        const offer = this.offerSelection?.primaryOffer;
        if (offer === undefined) {
            //Stringifying just to make sure values are coming through cleanly
            const offerCount = {
                config: !!this.config?.offer,
                item: this.config?.item?.offers?.length ?? false,
                href: !!this.href,
                selected: this.offerSelection?.selectedOffers?.length ?? false,
            };
            throw new Error(
            // eslint-disable-next-line max-len
            `unable to wrap link without offer.\n\t ${JSON.stringify(offerCount)}`);
        }
        const linkWrapperConfigs = this.getLinkWrapperConfigsForOffer(offer);
        this.wrappedHref = applyLinkWrappers(offer.url, linkWrapperConfigs, this.linkWrapperFns);
    }
    getLinkWrapperConfigsForOffer(offer) {
        const wrappers = identifyLinkWrappersForOffer(offer, this.config?.options?.linkWrappersByIdentifier);
        if (wrappers !== undefined) {
            return this.mergeLinkWrapperConfigs(wrappers);
        }
        return this.mergeLinkWrapperConfigs(this.config?.options?.linkWrappers ?? []);
    }
    mergeLinkWrapperConfigs(configs) {
        if (configs.length === 0) {
            return Object.values(this.pageLevelWrapperConfigs);
        }
        //@todo do this better in raven.  This is a hack
        let trackonomicsFound = false;
        configs.forEach((config, index) => {
            const pageConfig = this.pageLevelWrapperConfigs[config.vendor];
            if (isTrackonomicsLinkWrapperConfig(config) && isTrackonomicsLinkWrapperConfig(pageConfig)) {
                configs[index].options = { ...pageConfig.options, ...config.options };
                trackonomicsFound = true;
            }
        });
        if (trackonomicsFound === false && this.pageLevelWrapperConfigs['Trackonomics']) {
            configs.push(this.pageLevelWrapperConfigs['Trackonomics']);
        }
        return configs;
    }
}
__decorate([
    property(),
    __metadata("design:type", String)
], BaseCommerceLink.prototype, "href", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], BaseCommerceLink.prototype, "target", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], BaseCommerceLink.prototype, "rel", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], BaseCommerceLink.prototype, "dynamicAttrs", void 0);
__decorate([
    property(),
    __metadata("design:type", Object)
], BaseCommerceLink.prototype, "classnames", void 0);
__decorate([
    query('a.m-product--commerce-link'),
    __metadata("design:type", Object)
], BaseCommerceLink.prototype, "anchor", void 0);
__decorate([
    state(),
    __metadata("design:type", String)
], BaseCommerceLink.prototype, "wrappedHref", void 0);
//# sourceMappingURL=baseCommerceLink.js.map